import Header from "@/components/layout/Authenticated/Header";
import MenuHeader from "@/components/layout/Authenticated/MenuHeader";
import { IMenuLink, IMenuTypes } from "@/utils/models/core";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import HeaderMobile from "@/components/layout/Authenticated/HeaderMobile";
import LoaderSpinner from "@/components/common/LoadSpinner/LoadSpinner";
import { useTsRestQueryClient } from "@ts-rest/react-query";
import { opusClient } from "@/utils/rest-client/opus-client";
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react";
// import { OnServerToClientEvents } from "@/hooks/useSocket";
import { UserActivity } from "@/utils/validators/user-status.validator";
import { SvgClockIn, SvgLogout, SvgProfile } from "@/components/SvgIcon";
import { useFetchTimeZone } from "@/page/Authenticated/Dashboard/utils";
import { useTimeLocation } from "@/store/useTimeLocation";
import { ArrowRightLeft } from "lucide-react";
import storage from "@/utils/api/storage";
import useGeolocation from "@/hooks/useGeolocation";
import { OnServerToClientEvents, useSocket } from "@/hooks/useSocket";
import clsx from "clsx";

const menuHeader: IMenuLink[] = [
	{
		label: "Overview",
		to: "/dashboard",
	},
	{
		label: "Attendance",
		to: "/attendance",
	},
	{
		label: "Leave Management",
		to: "/leave-management",
	},
	{
		label: "Team Calendar",
		to: "/team-calendar",
	},
	{
		label: "Team Directory",
		to: "/team-directory",
	},
	{
		label: "Company Resources",
		to: "/company-resources",
	},
	{
		label: "Settings",
		to: '/company-settings',
		isAdminOnly: true
	},
];

const AuthLayout = () => {
	const {
		logout,
		user,
		isLoading,
		location: userLocation,
		accessToken,
	} = useAuth();
	const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
	const { setData } = useTimeLocation();
	const navigate = useNavigate();
	const apiQueryClient = useTsRestQueryClient(opusClient);
	const location = useLocation();
	const isFullWidthPage = ["/team-calendar"].includes(location.pathname);

	const { mutate: mutateUserStatus } =
		apiQueryClient.users.updateUserStatus.useMutation();

	const [, setMousePosition] = useState<{
		x: number;
		y: number;
	} | null>(null);

	const [, setIsIdle] = useState<boolean>(false);
	const [isMoving, setIsMoving] = useState(false);
	const [statusOpen, setStatusOpen] = useState<boolean>(false);

	// const { memoWeather: memoHomeWeather } = useGetTimeZone(
	// 	Number(location.lat),
	// 	Number(location.lng)
	// );

	const { data: dataHome } = useGeolocation(
		Number(userLocation.lat),
		Number(userLocation.lng)
	);

	const { timezone: homeTimeZone } = useFetchTimeZone(timeZone);

	const idleTimeLimit = 300000;
	const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const menu: IMenuTypes[] = [
		{
			icon: <SvgProfile className="w-5 h-5 mr-2" />,
			label: "View Profile",
			to: "/profile",
		},
		{
			icon: <SvgClockIn className="w-5 h-5 mr-2" />,
			label: "Change Status",
			onClick: () => {
				setStatusOpen(true);
			},
		},
		{
			icon: <SvgLogout className="w-5 h-5 mr-2" />,
			label: "Log Out",
			onClick: () => {
				logout();
				navigate("/login", { replace: true });
			},
		},
	];

	if (user?.role?.title === "Admin") {
		menu.splice(1, 0, {
			icon: <ArrowRightLeft className="w-5 h-5 mr-2" />,
			label: `Switch to ${storage.getItem("accessStatus") === "Staff" ? "Admin" : "Staff"
				}`,
			onClick: () => {
				if (storage.getItem("accessStatus") === "Staff") {
					storage.setItem("accessStatus", "Admin");
				} else {
					storage.setItem("accessStatus", "Staff");
				}
				window.location.reload();
			},
		});
	} else if (user?.isLeaveApprover) {
		menu.splice(1, 0, {
			icon: <ArrowRightLeft className="w-5 h-5 mr-2" />,
			label: `Switch to ${storage.getItem("accessStatus") === "Staff" ? "Approver" : "Staff"
				}`,
			onClick: () => {
				if (storage.getItem("accessStatus") === "Staff") {
					storage.setItem("accessStatus", "Approver");
				} else {
					storage.setItem("accessStatus", "Staff");
				}
				window.location.reload();
			},
		});
	}

	const handleMouseMove = useCallback(
		(event: MouseEvent<HTMLDivElement, MouseEvent>) => {
			const { clientX, clientY } = event;
			setMousePosition({ x: clientX, y: clientY });

			if (!isMoving) {
				setIsMoving(true);
				setIsIdle(false);

				mutateUserStatus({
					params: { userId: user!.id },
					body: {
						activity: UserActivity.Online,
					},
				});
			}

			if (idleTimeoutRef.current) {
				clearTimeout(idleTimeoutRef.current);
			}

			idleTimeoutRef.current = setTimeout(() => {
				if (isMoving) {
					setIsMoving(false);
					setIsIdle(true);

					mutateUserStatus({
						params: { userId: user!.id },
						body: {
							activity: UserActivity.Idle,
						},
					});
				}
			}, idleTimeLimit);
		},
		[isMoving, idleTimeLimit, mutateUserStatus, user]
	);

	const onUserStatusUpdate: OnServerToClientEvents["onUserStatusUpdate"] =
		useCallback(
			async (userId: string) => {
				if (userId === user?.id) {
					apiQueryClient.auth.me.fetchQuery(["me"]);
				}
			},
			[mutateUserStatus, apiQueryClient]
		);

	useSocket({
		accessToken: accessToken ?? "",
		onUserStatusUpdate,
	});

	// useEffect(() => {
	// 	handleMouseMove();
	// }, []);

	useEffect(() => {
		if (dataHome) {
			// const addressComponents = dataHome.results[0].address_components;
			// const stateComponent = addressComponents.find((component: any) =>
			// 	component.types.includes("administrative_area_level_1")
			// );
			// const countryComponent = addressComponents.find((component: any) =>
			// 	component.types.includes("country")
			// );
			setData(
				`${dataHome.address.city
					? dataHome.address.city
					: dataHome?.address.region
				}, ${dataHome.address.city} ${homeTimeZone}`
			);
		}
	}, [dataHome]);

	if (isLoading)
		return (
			<div className="flex justify-center items-center fixed h-full w-full z-50 bg-black bg-opacity-50">
				<LoaderSpinner className="w-10 h-20" />
			</div>
		);
	return (
		<main
			className="flex flex-col h-screen w-full bg-white md:h-screen"
			onMouseMove={(event) => handleMouseMove(event as any)}>
			<div className="hidden md:block">
				<Header
					menu={menu}
					setStatusOpen={setStatusOpen}
					statusOpen={statusOpen}
				/>
				<MenuHeader menu={menuHeader} />
			</div>
			<div className="md:hidden">
				<HeaderMobile />
			</div>
			<div className="md:flex flex-1 md:overflow-hidden">
				<div className="flex flex-1 flex-col">
					<div className="flex flex-1 overflow-y-auto h-full">
						<div className="flex flex-col flex-1 overflow-y-auto">
							<div
								className={clsx(
									"w-full mx-auto pb-10 md:pb-0  ",
									isFullWidthPage ? "" : "md:px-0 md:container mx-auto px-4"
								)}
								id="main">
								<Outlet />
							</div>
						</div>
					</div>
				</div>
			</div>
		</main>
	);
};

export default AuthLayout;
